/**
 * Which markup sessions in this window hold unconfirmed work
 * (`MarkupPersistenceSession.holdsUnsavedWork`): the base viewer's, Compare's, another host's
 * (Revision Narrative's review), and a closed viewer's while its last sends are on the way. Each
 * session reports itself (`reportUnsavedWork`).
 */
import { useSyncExternalStore } from "react";
import { useBeforeUnloadPrompt } from "../../../shell/leaveGuard";

const holders = new Set<object>();
const listeners = new Set<() => void>();

/** Records whether `holder` holds unsaved markup work now. */
export function reportUnsavedMarkupWork(holder: object, unsaved: boolean): void {
    if (holders.has(holder) === unsaved) return;
    if (unsaved) holders.add(holder);
    else holders.delete(holder);
    listeners.forEach((listener) => listener());
}

export function hasUnsavedMarkupWork(): boolean {
    return holders.size > 0;
}

export function subscribeUnsavedMarkupWork(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * While any markup in the window holds unsaved work, closing or reloading the tab asks first.
 * Mounted once per window (`AppProviders`), above every viewer, so it outlives a closed one.
 */
export function UnsavedMarkupPrompt(): null {
    useBeforeUnloadPrompt(useSyncExternalStore(subscribeUnsavedMarkupWork, hasUnsavedMarkupWork));
    return null;
}
